import type { RenderEnvironment } from "common/components/types";
import type { MapVisualType } from "generator/array-screens/map-screen-types";
import { MapListMode } from "generator/components/map-list-mode";
import { List as ListIcon, Map as MapIcon, Navigation2 } from "common/react-plucked";
import { ErrorBoundary } from "lib/error-boundary";
import { lazilyLoadedComponent } from "lib/react-lazy-loading";
import * as React from "react";
import ReactDOM from "react-dom";
import { withTheme } from "styled-components";
import { ScrollOverlayConsumer } from "../../../chrome/views/scroll-view/scroll-view-overlay";
import { AppUtilitiesConsumer } from "../../../lib/app-utility-context";
import type { RuntimeTheme } from "../../../lib/theme/theme";
import FloatingButton from "../../views/floating-button/floating-button";
import { BottomListItemWrapper, FloatingButtonWrapper, ListWrapper, MapListStyle } from "./map-list-style";
import type { MapMarker } from "./map-list-types";

const MapBoxMap = lazilyLoadedComponent("mapbox-map-list-map", () => import("./private/mapbox-map-list-map"));

interface SavedState {
    mode: MapListMode;
    selectedMarker: number | undefined;
}

interface BaseProps {
    markers: MapMarker[];
    markersLoading: boolean;
    listCount: number;
    listContent: React.ReactNode;
    itemFactory: (marker: MapMarker) => React.ReactNode;
    savedState: SavedState | undefined;
    saveState: (state: SavedState) => void;
    isSearchActive: boolean;
    visualType: MapVisualType;
    defaultMode: MapListMode;
    insideInlineList: boolean;
    showUserLocation: boolean;
    theme: RuntimeTheme;
    renderEnvironment: RenderEnvironment;

    // `true` means tap is handled, nothing left to be done.
    // `false` means map list must handle the tap by showing
    // the list item.
    onMarkerTapped: ((marker: MapMarker) => boolean) | undefined;
}

interface Props extends BaseProps {
    scrollOverlayID: string;
}

interface State {
    mode: MapListMode;
    selectedMarker: number | undefined;
    lastMarker: any;
    canShowBottomContainer: boolean;

    // To mitigate the effects of imperatively getting the portal element
    // we'll obtain it in cDM and update it if necessary in cDU
    portalElement: Element | null;
}

/**
 * This component is a dumpster fire at this point.
 * Unfortunate situations lead to this component expressing
 * many React anti-patterns:
 *
 * - It can copy props to state (at constructor)
 * - It uses UNSAFE lifecycle methods (componentWillReceiveProps)
 * - It can call setState in render (this.setState({ mode: MapListMode.List }))
 * - It uses ReactDOM.findDOMNode
 */
class MapListInner extends React.PureComponent<Props, State> {
    public state: State = {
        mode: MapListMode.Map,
        selectedMarker: undefined,
        lastMarker: undefined,
        canShowBottomContainer: true,
        portalElement: null,
    };

    private mapRefPriv = React.createRef<HTMLDivElement | null>();

    private get mapRef() {
        return this.mapRefPriv;
    }

    constructor(props: Props) {
        super(props);

        if (props.savedState === undefined) {
            this.state.mode = props.defaultMode;
        }
    }

    public componentDidMount() {
        if (this.props.savedState !== undefined) {
            let marker = this.props.savedState.selectedMarker;
            let lastMarker: MapMarker | undefined;
            if (marker !== undefined && marker >= this.props.markers.length) {
                marker = undefined;
            }
            if (marker !== undefined) {
                lastMarker = this.props.markers[marker];
            }
            this.setState({ mode: this.props.savedState.mode, selectedMarker: marker, lastMarker });
        }

        this.setPortalElement();
    }

    public componentDidUpdate(prevProps: Props) {
        if (this.props.defaultMode !== prevProps.defaultMode) {
            this.setState({ mode: this.props.defaultMode });
        }

        if (
            this.props.insideInlineList !== prevProps.insideInlineList ||
            this.props.scrollOverlayID !== prevProps.scrollOverlayID
        ) {
            this.setPortalElement();
        }
    }

    public UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (nextProps.markers !== this.props.markers && this.state.selectedMarker !== undefined) {
            if (nextProps.markers.length <= this.state.selectedMarker) {
                this.setState({ selectedMarker: undefined });
            } else {
                const newMarker = nextProps.markers[this.state.selectedMarker];
                const currentMarker = this.props.markers[this.state.selectedMarker];

                if (
                    newMarker.title !== currentMarker.title ||
                    newMarker.lat !== currentMarker.lat ||
                    newMarker.lng !== currentMarker.lng
                ) {
                    this.setState({ selectedMarker: undefined });
                }
            }
        }
    }

    public componentWillUnmount() {
        this.props.saveState({ mode: this.state.mode, selectedMarker: this.state.selectedMarker });
    }

    private setPortalElement = () => {
        let portalElement = document.getElementById(this.props.scrollOverlayID) as Element;

        if (this.props.insideInlineList && this.mapRef && this.mapRef.current) {
            portalElement = this.mapRef.current;
        }

        this.setState({ portalElement });
    };

    private onSwitch = () => {
        if (this.state.mode === MapListMode.List) {
            this.setState({ mode: MapListMode.Map, selectedMarker: undefined, lastMarker: undefined });
        } else {
            this.setState({ mode: MapListMode.List, selectedMarker: undefined });
        }
    };

    private onLocate = () => {
        this.locateCb();
    };

    private onMarkerSelected = (marker: MapMarker) => {
        const markers = this.props.markers;
        const i = markers.indexOf(marker);
        if (i !== -1) {
            if (this.props.onMarkerTapped?.(marker)) {
                this.setState({ selectedMarker: i, lastMarker: marker, canShowBottomContainer: false });
            } else {
                this.setState({ selectedMarker: i, lastMarker: marker, canShowBottomContainer: true });
            }
        } else {
            this.setState({ selectedMarker: undefined });
        }
    };

    private locateCb = () => {
        /**/
    };

    public render(): React.ReactNode {
        let content: React.ReactNode;
        const {
            itemFactory,
            listContent,
            isSearchActive,
            insideInlineList,
            showUserLocation,
            markers,
            markersLoading,
            theme,
            listCount,
            visualType,
            renderEnvironment,
        } = this.props;

        let mode = this.state.mode;
        if (isSearchActive || renderEnvironment.displayContext === "map-master") {
            mode = MapListMode.List;
        } else if (renderEnvironment.displayContext === "map-detail") {
            mode = MapListMode.Map;
        }

        const bottomOpen =
            this.state.lastMarker !== undefined &&
            this.state.selectedMarker !== undefined &&
            this.state.canShowBottomContainer;
        const Map = MapBoxMap;

        if (markersLoading && markers.length === 0) {
            content = null;
        } else if (mode === MapListMode.List || (markers.length === 0 && listCount > 0)) {
            if (mode !== MapListMode.List) {
                this.setState({ mode: MapListMode.List });
            }
            content = <ListWrapper isInline={insideInlineList}>{listContent}</ListWrapper>;
        } else {
            let bottomItem: React.ReactNode;
            if (this.state.lastMarker !== undefined && this.state.canShowBottomContainer) {
                bottomItem = (
                    <BottomListItemWrapper show={this.state.selectedMarker !== undefined}>
                        {itemFactory(this.state.lastMarker)}
                    </BottomListItemWrapper>
                );
            }

            content = (
                <ErrorBoundary>
                    <React.Suspense fallback={<div />}>
                        <AppUtilitiesConsumer>
                            {value => (
                                <Map
                                    visualType={visualType}
                                    markers={markers}
                                    selectedMarker={this.state.selectedMarker}
                                    onMarkerSelected={this.onMarkerSelected}
                                    onMapClick={() => this.setState({ selectedMarker: undefined })}
                                    appUtilities={value}
                                    showUserLocation={showUserLocation}
                                    setLocateCallback={cb => (this.locateCb = cb)}
                                />
                            )}
                        </AppUtilitiesConsumer>
                        {bottomItem}
                    </React.Suspense>
                </ErrorBoundary>
            );

            // Select first marker if in tablet mode.
            // The map screen doesn't have list items, so it doesn't use the regular
            // ##autoRunInTabletMode flag.
            const shouldPush =
                renderEnvironment.sizeClass === "tablet" &&
                renderEnvironment.displayContext === "master" &&
                !renderEnvironment.appEnvironment.isBuilder &&
                this.state.selectedMarker === undefined &&
                this.state.lastMarker === undefined &&
                markers.length > 0;
            if (shouldPush) {
                this.onMarkerSelected(markers[0]);
            }
        }

        const canShowPositionButton = renderEnvironment.displayContext !== "map-master";
        const canShowSwapButton = !renderEnvironment.displayContext.startsWith("map-");
        // The map buttons render slightly different and are pinned to different elements depending on the maps context
        //  1. Map in main layout.
        //      Always show the map / list toggle
        //      Only show user location button if it is in map mode and user location is enabled
        //      Pin the buttons to overlay scroll container
        //  2. Map is inline list
        //      Do not show the map / list toggle
        //      Only show user location button if it is in map mode and user location is enabled
        //      Pin the buttons to the map container (this.mapRef)
        const buttons = (
            <FloatingButtonWrapper bottomOpen={bottomOpen}>
                {showUserLocation && mode === MapListMode.Map && canShowPositionButton && (
                    <FloatingButton
                        dataTest="app-map-locate-button"
                        color="white"
                        yOffset={renderEnvironment.sizeClass !== "tablet" && !insideInlineList ? -68 : 0}
                        distance={20}
                        onClick={this.onLocate}
                        title="Show your location">
                        <Navigation2 color={theme.map.locationButtonForegroundColor} size={24} />
                    </FloatingButton>
                )}
                {!insideInlineList && canShowSwapButton && (
                    <FloatingButton
                        dataTest="app-map-list-toggle"
                        yOffset={renderEnvironment.sizeClass !== "tablet" && insideInlineList ? -68 : 0}
                        distance={20}
                        onClick={this.onSwitch}
                        title={`Show ${mode === MapListMode.List ? "map" : "list"}`}>
                        {mode === MapListMode.List ? <MapIcon size={22} /> : <ListIcon size={22} />}
                    </FloatingButton>
                )}
            </FloatingButtonWrapper>
        );

        let portal: React.ReactNode;
        if (this.state.portalElement !== null) {
            portal = ReactDOM.createPortal(buttons, this.state.portalElement);
        }

        return (
            <MapListStyle
                ref={this.mapRef as any}
                className="fullBleed"
                isInline={this.props.insideInlineList}
                isMapMode={mode === MapListMode.Map}>
                {content}
                {!isSearchActive && markers.length > 0 && portal}
            </MapListStyle>
        );
    }
}

function MapList(props: BaseProps) {
    return <ScrollOverlayConsumer>{id => <MapListInner {...props} scrollOverlayID={id} />}</ScrollOverlayConsumer>;
}

export default withTheme(MapList);
